import { Link } from 'react-router-dom';
import { BadgeCheck, Crown, BedDouble, Bath, Ruler, MapPin } from 'lucide-react';
import PriceDisplay from './PriceDisplay';
import { cardFrame, riseDelay, GOLD_BADGE } from './frames';

export const HOME_PLACEHOLDER = 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=60';

const LISTING = {
  for_rent:       { ar: 'للإيجار',   en: 'For Rent',       cls: 'bg-[#1E9BD9]/90' },
  for_sale:       { ar: 'للبيع',     en: 'For Sale',       cls: 'bg-[#F59E0B]/90' },
  for_investment: { ar: 'للاستثمار', en: 'For Investment', cls: 'bg-emerald-500/90' },
};

const EMIRATE_LABELS = {
  dubai: ['دبي', 'Dubai'], abu_dhabi: ['أبوظبي', 'Abu Dhabi'], sharjah: ['الشارقة', 'Sharjah'], ajman: ['عجمان', 'Ajman'],
  ras_al_khaimah: ['رأس الخيمة', 'Ras Al Khaimah'], umm_al_quwain: ['أم القيوين', 'Umm Al Quwain'], fujairah: ['الفجيرة', 'Fujairah'],
};

const Spec = ({ icon: Icon, children, dark }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium ${dark ? 'bg-white/5 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
    <Icon className="h-3 w-3 text-[#1E9BD9]" />
    {children}
  </span>
);

/** PropertyCard — Cyber-Luxe real-estate card: image with listing chip → title/location → spec chips → price/store. */
export default function PropertyCard({ item, isDarkMode, isAr, index = 0, testIdPrefix = 'property-card' }) {
  const img = (Array.isArray(item.images) && item.images[0]) || item.image || HOME_PLACEHOLDER;
  const title = item.title || `${item.propertyType || ''} ${item.emirate || ''}`.trim();
  const lt = LISTING[item.listingType] || LISTING.for_sale;
  const emKey = (item.emirate || '').toLowerCase();
  const emLabel = EMIRATE_LABELS[emKey] ? EMIRATE_LABELS[emKey][isAr ? 0 : 1] : item.emirate;
  const location = [item.area_name, emLabel].filter(Boolean).join(isAr ? '، ' : ', ');

  return (
    <Link
      to={`/property/${item.id}`}
      state={{ store: item.store, item }}
      className={`group relative block rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cgm-shimmer cgm-rise ${cardFrame(isDarkMode)}`}
      style={riseDelay(index)}
      data-testid={`${testIdPrefix}-${item.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={img} alt={title} onError={(e) => { e.currentTarget.src = HOME_PLACEHOLDER; }} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className={`absolute inset-0 bg-gradient-to-t ${isDarkMode ? 'from-[#181C2A] via-[#181C2A]/20' : 'from-black/40 via-transparent'} to-transparent`} />
        <span className={`absolute top-3 start-3 px-2.5 py-1 rounded-full text-[10px] font-bold text-white backdrop-blur ${lt.cls}`}>{isAr ? lt.ar : lt.en}</span>
        <div className="absolute top-3 end-3 flex items-center gap-1.5">
          {item.featured && <span className={GOLD_BADGE}><Crown className="h-3 w-3" />{isAr ? 'مميز' : 'VIP'}</span>}
          {item.store?.verified && <span className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center"><BadgeCheck className="h-4 w-4 text-[#1E9BD9]" /></span>}
        </div>
        {location && (
          <div className="absolute bottom-3 start-3 end-3 flex items-center gap-1 text-[11px] font-medium text-white drop-shadow">
            <MapPin className="h-3.5 w-3.5 text-[#F5A962]" />
            <span className="truncate">{location}</span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 cgm-scanline opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>

      <div className="p-4 sm:p-5">
        <h3 className={`text-base sm:text-lg font-bold line-clamp-1 mb-2.5 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{title}</h3>
        <div className="flex flex-wrap gap-1.5 mb-3.5">
          {item.bedrooms != null && <Spec icon={BedDouble} dark={isDarkMode}>{item.bedrooms} {isAr ? 'غرف' : 'BR'}</Spec>}
          {item.bathrooms != null && <Spec icon={Bath} dark={isDarkMode}>{item.bathrooms} {isAr ? 'حمام' : 'BA'}</Spec>}
          {item.area != null && <Spec icon={Ruler} dark={isDarkMode}>{Number(item.area).toLocaleString()} ft²</Spec>}
        </div>
        <div className={`flex items-center justify-between gap-2 pt-3 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
          <PriceDisplay item={item} locale={isAr ? 'ar' : 'en'} size="sm" />
          <span className={`text-xs truncate max-w-[120px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{item.store?.name || '—'}</span>
        </div>
      </div>
    </Link>
  );
}
